import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Bookmark, Flag, Menu, Snowflake, UserRound, Users } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganizerPortal } from '../../contexts/OrganizerPortalContext';
import radsportMark from '../../assets/brand/radsport-mark.svg';
import { challengePhase, challengeToday } from '@radevents/shared';
import { useChallengeSummaryState } from '../WinterchallengePromoBanner';
import { PFLICHT_LINKS } from './pflichtLinks';
import { SOCIAL_LINKS, InstagramGlyph, FacebookGlyph } from '../SocialIcons';
import { useMeineEventsZahl, useNeueNews } from '../../hooks/useKopfHinweise';

/**
 * Public top navigation bar for the redesigned consumer site.
 * Matches the Claude-Design "Web Prototyp" header:
 * sticky, blurred warm-neutral bar with the Radsport-Events mark, wortmark, nav links and CTA.
 */
interface PublicHeaderProps {
  /** When provided (Landing), the "Event eintragen" CTA opens the inline proposal flow. */
  onProposeEvent?: () => void;
  /** Phone-only extras rendered right of the CTA (Landing: the filter button). */
  mobileActions?: React.ReactNode;
  /** Suchfeld im Kopf, nur am Rechner (Landing mit Karte). */
  searchSlot?: React.ReactNode;
}

const NAV = [
  { label: 'Events', to: '/', match: (p: string) => p === '/' || p === '/permanente-events' || p.startsWith('/events') || p.startsWith('/rennrad-events') || p.startsWith('/gravel-events') || p.startsWith('/mtb-events') || p.startsWith('/rtf-events') },
  // Saisonal, auf Phils Wunsch vom 25.10.2026 trotz der Regel aus `docs/konzept-navigation.md`:
  // an zweiter Stelle, nur solange die Challenge läuft oder bevorsteht, danach verschwindet sie.
  { label: 'Winterchallenge', to: '/winterchallenge', match: (p: string) => p.startsWith('/winterchallenge'), seasonal: true },
  // Steht kein Profi-Rennen an, macht es der Winterchallenge Platz (`summary.proRacesActive`, der Server entscheidet).
  { label: 'Profi-Rennen', to: '/rennkalender', match: (p: string) => p.startsWith('/rennkalender') || p === '/radsport-im-tv', proRaces: true },
  { label: 'News', to: '/news', match: (p: string) => p.startsWith('/news') },
];

/** „Meine Events“ steht seit dem 28.10.2026 rechts zwischen „Event eintragen“ und dem Konto, wie Merkliste und Buchungen bei
 *  GetYourGuide: persönliche Bereiche rechts, Inhalte in der Mitte. */
const MEINE_EVENTS = { to: '/favorites', match: (p: string) => p.startsWith('/favorites') };

/**
 * Menü (☰) ganz rechts am Rechner, dasselbe wie „Mehr“ am Handy (seit 28.10.2026 gleichgezogen):
 * Veranstalter, Profi-Rennen, wenn es nicht schon in der Navigation steht, dann still Impressum,
 * Datenschutz, Kontakt, Über uns. Die Winterchallenge fehlt, sie steht schon in der Navigation;
 * Instagram und Facebook schweben unten rechts. Eine Fußzeile gibt es seit dem 30.10.2026 nicht
 * mehr; „Für Veranstalter“ steht als Hinweis im Verzeichnis unter den Event-Listen.
 */
const MehrMenue: React.FC<{ veranstalterZiel: string; profiRennen: boolean }> = ({ veranstalterZiel, profiRennen }) => {
  const [offen, setOffen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => { setOffen(false); }, [location.pathname]);
  useEffect(() => {
    if (!offen) return;
    const zu = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOffen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOffen(false); };
    document.addEventListener('mousedown', zu);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', zu); document.removeEventListener('keydown', esc); };
  }, [offen]);
  return (
    <div ref={ref} className="kopf-mehr">
      <button type="button" className={`kopf-rund kopf-mehr-knopf${location.pathname.startsWith('/veranstalter') ? ' kopf-mehr-knopf--aktiv' : ''}`} aria-expanded={offen} aria-haspopup="menu" aria-label="Menü" title="Menü" onClick={() => setOffen(o => !o)}>
        <Menu size={20} aria-hidden />
      </button>
      {offen && (
        <div className="kopf-mehr-menue" role="menu">
          {/* Für einen Verwalter zeigt „Veranstalter“ auf seinen eigenen Bereich, sonst ins Verzeichnis (`navTo`). */}
          <Link to={veranstalterZiel} role="menuitem"><Users size={17} aria-hidden />Veranstalter</Link>
          {profiRennen && <Link to="/rennkalender" role="menuitem"><Flag size={17} aria-hidden />Profi-Rennen</Link>}
          <hr />
          <div className="kopf-mehr-pflicht">
            {PFLICHT_LINKS.map(l => <Link key={l.to} to={l.to} role="menuitem">{l.label}</Link>)}
          </div>
        </div>
      )}
    </div>
  );
};

const PublicHeader: React.FC<PublicHeaderProps> = ({ onProposeEvent, mobileActions, searchSlot }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdminOrModerator } = useAuth();
  const { isOrganizer } = useOrganizerPortal();
  const path = location.pathname;
  // Solange der Stand lädt, stehen die saisonalen Punkte noch nicht, damit nichts springt. Erst
  // mit der Antwort entscheidet sich, ob „Winterchallenge“ und „Profi-Rennen“ dastehen; ohne
  // Saison bleibt es beim Rennkalender.
  const { summary: challenge, loaded } = useChallengeSummaryState();
  const challengeActive = challenge != null && challengePhase(challenge, challengeToday()) !== 'after';
  // Fehlt das Feld (älteres Backend), bleibt „Profi-Rennen“ stehen.
  const proActive = challenge?.proRacesActive ?? true;
  const neueNews = useNeueNews();
  const meineZahl = useMeineEventsZahl(isAuthenticated);
  const meineAktiv = MEINE_EVENTS.match(path);
  const nav = NAV.filter(n =>
    'seasonal' in n ? challengeActive
    : 'proRaces' in n ? loaded && (!challengeActive || proActive)
    : true);

  /**
   * „Veranstalter" führt einen Verwalter zu **seinem** Verein, alle anderen ins Verzeichnis.
   *
   * Wer einen Verein pflegt, sucht hinter diesem Wort seine Arbeitsfläche und nicht eine Liste
   * mit tausend fremden Vereinen, in der er sich selbst erst suchen müsste. Ziel ist die Tür
   * `/veranstalter-bereich` und nicht direkt `/veranstalter/:id`: sie kennt den Fall „mehrere
   * Vereine" und ist eine feste Adresse, die auch dann stimmt, wenn die Verknüpfungen beim
   * ersten Aufbau der Seite noch geladen werden.
   */
  const navTo = (to: string) =>
    (to === '/veranstalter' && isOrganizer ? '/veranstalter-bereich' : to);
  // Auf Phones bleibt der CTA dem Events-Tab vorbehalten; auf Desktop steht er überall.
  const onEventsTab = NAV[0].match(path);

  // Die Redaktion legt Events direkt an. Über den Vorschlagsweg landete ihr eigenes Event sonst
  // in der Prüf-Queue, die sie anschließend selbst abarbeiten müsste.
  const handlePropose = () => {
    if (isAdminOrModerator()) navigate(`/events/manage?neu=1${path === '/permanente-events' ? '&permanent=1' : ''}`);
    else if (onProposeEvent) onProposeEvent();
    else navigate('/?eintragen=1');
  };

  return (
    <header
      className="public-header"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 30,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 28,
        padding: '12px 40px',
        background: 'rgba(245,243,243,0.9)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        borderBottom: '1px solid #E5E1E1',
      }}
    >
      <Link to="/" aria-label="Radsport Events, zur Startseite" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0 }}>
        <img src={radsportMark} alt="" width={32} height={32} style={{ display: 'block', objectFit: 'contain' }} />
        <span className="header-wordmark" style={{ fontFamily: "'Archivo',sans-serif", fontStyle: 'italic', fontWeight: 800, fontSize: 21, letterSpacing: '-0.01em', color: '#4A4A4A' }}>
          Radsport<span style={{ color: '#922C57' }}> Events</span>
        </span>
      </Link>

      {/* Suche im Kopf (Startseite am Rechner): links neben der Navigation, die dann nach rechts rückt. */}
      {searchSlot && <div className="hide-mobile" style={{ flex: '1 1 auto', display: 'flex', minWidth: 0 }}>{searchSlot}</div>}

      <nav
        className="public-nav"
        style={{ display: 'flex', alignItems: 'center', gap: 28, flexShrink: 0, fontFamily: "'Hanken Grotesk',sans-serif", fontSize: 17, fontWeight: 600, whiteSpace: 'nowrap' }}
      >
        {nav.map((n) => {
          const active = n.match(path);
          const winter = 'seasonal' in n;
          const newsPunkt = n.to === '/news' && neueNews;
          return (
            <Link key={n.to} to={navTo(n.to)} className="kopf-nav-punkt" style={{ color: active ? '#922C57' : '#4A4A4A' }}>
              {/* Winterchallenge: Schneeflocke in Markenfarbe. */}
              {winter && <Snowflake size={17} aria-hidden className="kopf-nav-symbol" />}
              {n.label}
              {/* Neuer Artikel in den letzten drei Tagen, für alle gleich, ohne Speichern im Browser. */}
              {newsPunkt && <span className="kopf-nav-punkt-neu" title="Neuer Artikel" aria-label="neuer Artikel" />}
            </Link>
          );
        })}
      </nav>

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexShrink: 0 }}>
        <button
          className={`header-cta${onEventsTab ? '' : ' hide-mobile'}`}
          onClick={handlePropose}
          style={{ background: '#2C2C2C', color: '#fff', border: 'none', padding: '11px 20px', borderRadius: 999, fontFamily: "'Hanken Grotesk',sans-serif", fontWeight: 700, fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          <span className="hide-mobile">Event eintragen</span>
          <span className="hide-desktop">+ Eintragen</span>
        </button>
        <Link to={MEINE_EVENTS.to} className={`kopf-meine hide-mobile${meineAktiv ? ' kopf-meine--aktiv' : ''}`}
          aria-current={meineAktiv ? 'page' : undefined}>
          <Bookmark size={18} aria-hidden fill={meineAktiv ? 'currentColor' : 'none'} />
          Meine Events
          {meineZahl != null && meineZahl > 0 && <span className="kopf-meine-zahl" aria-label={`${meineZahl} kommende`}>{meineZahl}</span>}
        </Link>
        {/* Konto als Symbol ganz rechts (27.10.2026), wie bei Airbnb und Strava: angemeldet zum Profil, sonst zur Anmeldung. */}
        <Link className="header-signin kopf-rund" to={isAuthenticated ? '/profile' : '/login'}
          aria-label={isAuthenticated ? 'Profil' : 'Anmelden'} title={isAuthenticated ? 'Profil' : 'Anmelden'}>
          <UserRound size={20} aria-hidden />
        </Link>
        {/* Menü ganz rechts wie bei Airbnb: Veranstalter, Impressum, Datenschutz, Kontakt. Nur am Rechner,
            am Handy übernimmt „Mehr“ in der Tab-Leiste. */}
        <span className="hide-mobile"><MehrMenue veranstalterZiel={navTo('/veranstalter')} profiRennen={!nav.some(n => n.to === '/rennkalender')} /></span>
        {/* Instagram und Facebook schwebend unten rechts (Rechner): sichtbar auf jeder Seite, ohne
            zwischen den Menüpunkten zu stehen, die auf der Seite bleiben. Per Portal an den Body: der
            Kopf hat `backdrop-filter`, darin bezöge sich `position: fixed` auf den Kopf. */}
        {createPortal(<span className="sozial-schwebend hide-mobile">
          <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" aria-label="Radsport Events auf Instagram" title="Instagram"><InstagramGlyph size={20} strokeWidth={1.8} /></a>
          <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" aria-label="Radsport Events auf Facebook" title="Facebook"><FacebookGlyph size={20} /></a>
        </span>, document.body)}
        {mobileActions && <span className="hide-desktop" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>{mobileActions}</span>}
      </div>
    </header>
  );
};

export default PublicHeader;
